import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Sparkles, Briefcase, Users, Workflow, Mail, MessageCircle, Globe,
  TrendingUp, Zap, ShieldCheck, ArrowRight, Loader2, Play, RefreshCw, CheckCircle2, Download,
} from 'lucide-react';
import { Button } from '../components/ui/button';
import { toast } from 'sonner';

const TONE_PILLAR = {
  emerald: { from: 'from-emerald-500/10', border: 'border-emerald-500/30', borderHover: 'hover:border-emerald-500/50', iconBg: 'bg-emerald-500/15', iconBorder: 'border-emerald-500/30', iconText: 'text-emerald-300', check: 'text-emerald-400' },
  violet: { from: 'from-violet-500/10', border: 'border-violet-500/30', borderHover: 'hover:border-violet-500/50', iconBg: 'bg-violet-500/15', iconBorder: 'border-violet-500/30', iconText: 'text-violet-300', check: 'text-violet-400' },
  sky: { from: 'from-sky-500/10', border: 'border-sky-500/30', borderHover: 'hover:border-sky-500/50', iconBg: 'bg-sky-500/15', iconBorder: 'border-sky-500/30', iconText: 'text-sky-300', check: 'text-sky-400' },
  amber: { from: 'from-amber-500/10', border: 'border-amber-500/30', borderHover: 'hover:border-amber-500/50', iconBg: 'bg-amber-500/15', iconBorder: 'border-amber-500/30', iconText: 'text-amber-300', check: 'text-amber-400' },
};

const TONE_STAT = {
  emerald: { border: 'border-emerald-500/20', text: 'text-emerald-300' },
  violet: { border: 'border-violet-500/20', text: 'text-violet-300' },
  sky: { border: 'border-sky-500/20', text: 'text-sky-300' },
  amber: { border: 'border-amber-500/20', text: 'text-amber-300' },
  rose: { border: 'border-rose-500/20', text: 'text-rose-300' },
};

const Pillar = ({ icon: Icon, title, sub, tone, points, testId }) => {
  const t = TONE_PILLAR[tone] || TONE_PILLAR.violet;
  return (
    <div
      data-testid={testId}
      className={`bg-gradient-to-br ${t.from} to-zinc-900 border ${t.border} ${t.borderHover} rounded-2xl p-5 transition-colors`}
    >
      <div className={`w-10 h-10 rounded-xl ${t.iconBg} border ${t.iconBorder} flex items-center justify-center mb-3`}>
        <Icon className={`w-5 h-5 ${t.iconText}`} />
      </div>
      <h3 className="text-lg font-semibold mb-1">{title}</h3>
      <p className="text-xs text-zinc-400 mb-3">{sub}</p>
      <ul className="space-y-1">
        {points.map((p, i) => (
          <li key={i} className="text-[11px] text-zinc-300 flex items-start gap-1.5">
            <CheckCircle2 className={`w-3 h-3 ${t.check} mt-0.5 flex-shrink-0`} /> {p}
          </li>
        ))}
      </ul>
    </div>
  );
};

const Pain = ({ icon: Icon, title, body }) => (
  <div className="bg-zinc-900/60 border border-zinc-800 rounded-xl p-4">
    <Icon className="w-5 h-5 text-rose-400 mb-2" />
    <h4 className="text-sm font-semibold mb-1">{title}</h4>
    <p className="text-[11px] text-zinc-400">{body}</p>
  </div>
);

const Stat = ({ value, label, tone = 'violet' }) => {
  const t = TONE_STAT[tone] || TONE_STAT.violet;
  return (
    <div className={`bg-zinc-900/60 border ${t.border} rounded-xl p-4 text-center`}>
      <p className={`text-3xl font-bold ${t.text}`}>{value}</p>
      <p className="text-[10px] uppercase tracking-wider text-zinc-500 mt-1">{label}</p>
    </div>
  );
};

const DemoPitch = () => {
  const navigate = useNavigate();
  const [seedStatus, setSeedStatus] = useState({ seeded: false });
  const [seeding, setSeeding] = useState(false);
  const [resetting, setResetting] = useState(false);
  const [summary, setSummary] = useState(null);
  const token = localStorage.getItem('token');
  const API = process.env.REACT_APP_BACKEND_URL;

  const refresh = async () => {
    try {
      const [s, sum] = await Promise.all([
        fetch(`${API}/api/demo/status`, { headers: { Authorization: `Bearer ${token}` } }).then((r) => r.json()).catch(() => ({ seeded: false })),
        fetch(`${API}/api/marketing-suite/summary`, { headers: { Authorization: `Bearer ${token}` } }).then((r) => r.json()).catch(() => null),
      ]);
      setSeedStatus(s);
      setSummary(sum);
    } catch { /* noop */ }
  };

  useEffect(() => { if (token) refresh(); /* eslint-disable-next-line */ }, [token]);

  const seed = async () => {
    setSeeding(true);
    try {
      const res = await fetch(`${API}/api/demo/seed`, {
        method: 'POST', headers: { Authorization: `Bearer ${token}` },
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data?.detail || 'Seed failed');
      toast.success(`Demo data ready · ${data.summary.leads} leads, ${data.summary.deals} deals, ${data.summary.email_campaigns} campaigns`);
      await refresh();
    } catch (e) { toast.error(e.message); }
    finally { setSeeding(false); }
  };

  const reset = async () => {
    if (!window.confirm('Wipe demo data and start clean?')) return;
    setResetting(true);
    try {
      const res = await fetch(`${API}/api/demo/reset`, {
        method: 'POST', headers: { Authorization: `Bearer ${token}` },
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data?.detail || 'Reset failed');
      toast.success(`Removed ${data.deleted} demo records`);
      await refresh();
    } catch (e) { toast.error(e.message); }
    finally { setResetting(false); }
  };

  return (
    <div className="min-h-screen bg-zinc-950 text-white" data-testid="demo-pitch-page">
      {/* Hero */}
      <header className="relative overflow-hidden border-b border-zinc-800">
        <div className="absolute inset-0 bg-gradient-to-br from-violet-500/10 via-transparent to-emerald-500/10 pointer-events-none" />
        <div className="relative max-w-6xl mx-auto px-6 md:px-10 py-14 md:py-20">
          <span className="inline-flex items-center gap-1.5 text-[11px] px-2.5 py-1 rounded-full border border-violet-500/40 bg-violet-500/10 text-violet-200 uppercase tracking-wider">
            <Sparkles className="w-3 h-3" /> Snagcart · Investor Demo
          </span>
          <h1 className="mt-4 text-4xl sm:text-5xl lg:text-6xl font-heading font-bold leading-tight">
            The <span className="text-violet-300">SMB operating system</span><br />
            that finds, sells, and grows customers — on autopilot.
          </h1>
          <p className="mt-4 text-base text-zinc-300 max-w-2xl">
            Snagcart unifies four products SMBs normally cobble together — Lead Scraping, CRM, AI Website Builder,
            and Marketing/Automation — into one workspace. One login, one customer graph, one bill.
          </p>
          <div className="mt-7 flex flex-wrap gap-3">
            <Button
              size="lg"
              onClick={seed}
              disabled={seeding}
              data-testid="demo-seed-btn"
              className="h-11 px-5 bg-violet-600 hover:bg-violet-500 text-white text-sm font-semibold"
            >
              {seeding ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Sparkles className="w-4 h-4 mr-2" />}
              {seedStatus.seeded ? 'Re-seed demo data' : 'Seed demo data (1-click)'}
            </Button>
            <Button
              size="lg"
              onClick={() => navigate('/dashboard')}
              data-testid="demo-launch-btn"
              className="h-11 px-5 bg-emerald-500/15 border border-emerald-500/40 text-emerald-200 hover:bg-emerald-500/25 text-sm font-semibold"
            >
              <Play className="w-4 h-4 mr-2" /> Launch live demo
            </Button>
            <a
              href="/snagcart-pitch-package.zip"
              download="snagcart-pitch-package.zip"
              data-testid="demo-download-pitch-btn"
              className="inline-flex items-center gap-1.5 h-11 px-4 rounded-md text-sm font-semibold border border-amber-500/40 bg-amber-500/10 text-amber-200 hover:bg-amber-500/20"
            >
              <Download className="w-4 h-4" /> Download full pitch (.zip)
            </a>
            {seedStatus.seeded && (
              <button
                onClick={reset}
                disabled={resetting}
                data-testid="demo-reset-btn"
                className="h-11 px-4 rounded-md text-xs text-zinc-400 hover:text-rose-300 inline-flex items-center gap-1.5"
              >
                {resetting ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <RefreshCw className="w-3.5 h-3.5" />}
                Reset demo
              </button>
            )}
          </div>
          {seedStatus.seeded && (
            <p className="mt-3 text-[11px] text-emerald-300 inline-flex items-center gap-1.5">
              <CheckCircle2 className="w-3 h-3" /> Demo dataset loaded · 15 leads · 5 deals · 2 campaigns · 1 active automation
            </p>
          )}
        </div>
      </header>

      {/* Problem */}
      <section className="max-w-6xl mx-auto px-6 md:px-10 py-12">
        <p className="text-[10px] uppercase tracking-wider text-rose-300 mb-2">The problem</p>
        <h2 className="text-2xl md:text-3xl font-semibold mb-6 max-w-3xl">
          The average SMB stitches together <span className="text-rose-300">8+ tools</span> just to land and keep a customer.
        </h2>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
          <Pain icon={Briefcase} title="Lead gen lives in spreadsheets" body="Scraping tools, Excel exports, manual CRM imports — leads die in transit." />
          <Pain icon={Workflow} title="No system connects the dots" body="CRM, website, email, automations are 4 SaaS bills with broken integrations." />
          <Pain icon={Globe} title="Websites take weeks" body="Cookie-cutter builders, no automation, no CRM — SMBs ship slow and look generic." />
        </div>
      </section>

      {/* Solution */}
      <section className="max-w-6xl mx-auto px-6 md:px-10 py-12">
        <p className="text-[10px] uppercase tracking-wider text-violet-300 mb-2">The solution</p>
        <h2 className="text-2xl md:text-3xl font-semibold mb-6 max-w-3xl">
          One operating system. Four pillars. <span className="text-violet-300">All native, all connected.</span>
        </h2>
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-3">
          <Pillar
            testId="pillar-leadscraper"
            icon={Briefcase}
            tone="emerald"
            title="Lead Scraper"
            sub="Find local prospects in seconds"
            points={[
              'Google-Places powered local search',
              'Session history + saved searches',
              'One-click push to native CRM',
            ]}
          />
          <Pillar
            testId="pillar-crm"
            icon={Users}
            tone="violet"
            title="Native CRM"
            sub="Contacts · Leads · Kanban deals"
            points={[
              'Drag opportunities through stages',
              'Lead → Deal conversion',
              'CSV export · source attribution',
            ]}
          />
          <Pillar
            testId="pillar-builder"
            icon={Globe}
            tone="sky"
            title="Snagcart Builder"
            sub="AI website + ecommerce builder"
            points={[
              'Templates · Snagcart Coder · Live preview',
              '1-click deploy: Vercel/Netlify/GitHub',
              'ZIP export · custom domains',
            ]}
          />
          <Pillar
            testId="pillar-marketing"
            icon={Workflow}
            tone="amber"
            title="Marketing & Automation"
            sub="Email · SMS · WhatsApp · Workflows"
            points={[
              'Analytics & A/B testing',
              'Trigger → action workflows',
              'Suppression lists & blacklists',
            ]}
          />
        </div>
      </section>

      {/* Live KPIs from seeded data */}
      {summary && (
        <section className="max-w-6xl mx-auto px-6 md:px-10 py-12">
          <p className="text-[10px] uppercase tracking-wider text-emerald-300 mb-2">Live on this account</p>
          <h2 className="text-2xl md:text-3xl font-semibold mb-6 max-w-3xl">
            Real numbers, served from <span className="text-emerald-300">your own backend</span>.
          </h2>
          <div className="grid grid-cols-2 md:grid-cols-5 gap-3">
            <Stat tone="emerald" value={summary.contacts?.crm_leads ?? 0} label="CRM Leads" />
            <Stat tone="violet" value={summary.email?.campaigns ?? 0} label="Email campaigns" />
            <Stat tone="sky" value={(summary.email?.sent ?? 0).toLocaleString()} label="Emails sent" />
            <Stat tone="amber" value={`${summary.email?.open_rate ?? 0}%`} label="Avg open rate" />
            <Stat tone="rose" value={summary.sms?.campaigns ?? 0} label="SMS campaigns" />
          </div>
        </section>
      )}

      {/* Why now / moat */}
      <section className="max-w-6xl mx-auto px-6 md:px-10 py-12">
        <p className="text-[10px] uppercase tracking-wider text-amber-300 mb-2">Why Snagcart wins</p>
        <h2 className="text-2xl md:text-3xl font-semibold mb-6 max-w-3xl">
          The moat is the <span className="text-amber-300">unified graph</span>.
        </h2>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
          <div className="bg-zinc-900/60 border border-zinc-800 rounded-xl p-5">
            <Zap className="w-5 h-5 text-amber-300 mb-2" />
            <h4 className="text-sm font-semibold mb-1">Speed of execution</h4>
            <p className="text-[11px] text-zinc-400">AI-built sites, AI-drafted campaigns, AI-routed leads. Days, not weeks.</p>
          </div>
          <div className="bg-zinc-900/60 border border-zinc-800 rounded-xl p-5">
            <TrendingUp className="w-5 h-5 text-emerald-300 mb-2" />
            <h4 className="text-sm font-semibold mb-1">Compounding data</h4>
            <p className="text-[11px] text-zinc-400">Every lead, deal, email, automation strengthens the same customer graph.</p>
          </div>
          <div className="bg-zinc-900/60 border border-zinc-800 rounded-xl p-5">
            <ShieldCheck className="w-5 h-5 text-sky-300 mb-2" />
            <h4 className="text-sm font-semibold mb-1">One vendor, one bill</h4>
            <p className="text-[11px] text-zinc-400">Replaces 8+ SaaS tools. Cuts cost, support tickets, and integration fatigue.</p>
          </div>
        </div>
      </section>

      {/* CTA / suggested walkthrough */}
      <section className="max-w-6xl mx-auto px-6 md:px-10 py-12">
        <div className="bg-gradient-to-br from-violet-500/15 via-zinc-900 to-emerald-500/15 border border-violet-500/30 rounded-3xl p-8 md:p-10">
          <h2 className="text-2xl md:text-3xl font-semibold mb-2">Suggested live walkthrough (3 minutes)</h2>
          <p className="text-sm text-zinc-300 mb-6 max-w-2xl">
            Hit "Seed demo data" above, then click through these screens to show the loop end-to-end.
          </p>
          <ol className="space-y-3 text-sm">
            <li className="flex items-start gap-3">
              <span className="w-6 h-6 rounded-full bg-violet-500/15 border border-violet-500/40 text-violet-200 text-xs flex items-center justify-center flex-shrink-0">1</span>
              <button onClick={() => navigate('/lead-scraper')} className="text-left hover:text-violet-200 inline-flex items-center gap-1.5">
                <Briefcase className="w-3.5 h-3.5 text-emerald-300" /> Lead Scraper — show 3 session history rows pulling local SMBs
                <ArrowRight className="w-3 h-3" />
              </button>
            </li>
            <li className="flex items-start gap-3">
              <span className="w-6 h-6 rounded-full bg-violet-500/15 border border-violet-500/40 text-violet-200 text-xs flex items-center justify-center flex-shrink-0">2</span>
              <button onClick={() => navigate('/marketing-suite/crm')} className="text-left hover:text-violet-200 inline-flex items-center gap-1.5">
                <Users className="w-3.5 h-3.5 text-violet-300" /> CRM → Pipeline — drag a deal across stages
                <ArrowRight className="w-3 h-3" />
              </button>
            </li>
            <li className="flex items-start gap-3">
              <span className="w-6 h-6 rounded-full bg-violet-500/15 border border-violet-500/40 text-violet-200 text-xs flex items-center justify-center flex-shrink-0">3</span>
              <button onClick={() => navigate('/marketing-suite')} className="text-left hover:text-violet-200 inline-flex items-center gap-1.5">
                <Mail className="w-3.5 h-3.5 text-amber-300" /> Marketing Suite — KPI tiles + 2 sent email campaigns with open/click rates
                <ArrowRight className="w-3 h-3" />
              </button>
            </li>
            <li className="flex items-start gap-3">
              <span className="w-6 h-6 rounded-full bg-violet-500/15 border border-violet-500/40 text-violet-200 text-xs flex items-center justify-center flex-shrink-0">4</span>
              <button onClick={() => navigate('/automations')} className="text-left hover:text-violet-200 inline-flex items-center gap-1.5">
                <Workflow className="w-3.5 h-3.5 text-rose-300" /> Automations — hit "Test run" on the seeded workflow → live step logs
                <ArrowRight className="w-3 h-3" />
              </button>
            </li>
            <li className="flex items-start gap-3">
              <span className="w-6 h-6 rounded-full bg-violet-500/15 border border-violet-500/40 text-violet-200 text-xs flex items-center justify-center flex-shrink-0">5</span>
              <button onClick={() => navigate('/dashboard')} className="text-left hover:text-violet-200 inline-flex items-center gap-1.5">
                <Globe className="w-3.5 h-3.5 text-sky-300" /> Dashboard — show the seeded Brew Haven site with Vercel deploy URL
                <ArrowRight className="w-3 h-3" />
              </button>
            </li>
            <li className="flex items-start gap-3">
              <span className="w-6 h-6 rounded-full bg-violet-500/15 border border-violet-500/40 text-violet-200 text-xs flex items-center justify-center flex-shrink-0">★</span>
              <span className="text-zinc-300">
                Close on <span className="text-amber-200">why now</span> — AI + zero-config SMB stack = the right moment to build the operating system small businesses missed.
              </span>
            </li>
          </ol>
          <div className="mt-6 flex flex-wrap gap-3">
            <Button onClick={() => navigate('/dashboard')} className="bg-violet-600 hover:bg-violet-500" data-testid="demo-cta-dashboard">
              Start the walkthrough <ArrowRight className="w-4 h-4 ml-1.5" />
            </Button>
            <a
              href="/PRESENTER_SCRIPT.md"
              target="_blank"
              rel="noreferrer"
              className="inline-flex items-center gap-1.5 h-10 px-4 rounded-md text-xs font-semibold border border-zinc-700 text-zinc-300 hover:border-zinc-600 hover:text-white"
              data-testid="demo-script-link"
            >
              <MessageCircle className="w-3.5 h-3.5" /> Open presenter script
            </a>
          </div>

          <div
            className="mt-8 pt-6 border-t border-zinc-800"
            data-testid="demo-video-package"
          >
            <div className="flex items-center gap-2 mb-2">
              <Play className="w-4 h-4 text-violet-300" />
              <h3 className="text-sm font-semibold text-zinc-200">Competition demo videos</h3>
            </div>
            <p className="text-xs text-zinc-400 mb-3">
              Captioned MP4s for YC (~2 min) and Emergent (~5 min). Same product loop as this walkthrough.
            </p>
            <div className="flex flex-wrap gap-2">
              <a
                href="/pitch/demo-video/snagcart-demo-yc.mp4"
                target="_blank"
                rel="noreferrer"
                className="inline-flex items-center gap-1.5 h-9 px-3 rounded-md text-xs font-semibold border border-violet-500/40 text-violet-200 hover:bg-violet-500/10"
                data-testid="demo-video-yc"
              >
                <Download className="w-3.5 h-3.5" /> YC cut (~2:05)
              </a>
              <a
                href="/pitch/demo-video/snagcart-demo-emergent.mp4"
                target="_blank"
                rel="noreferrer"
                className="inline-flex items-center gap-1.5 h-9 px-3 rounded-md text-xs font-semibold border border-zinc-700 text-zinc-300 hover:border-zinc-600 hover:text-white"
                data-testid="demo-video-emergent"
              >
                <Download className="w-3.5 h-3.5" /> Emergent cut (~5:17)
              </a>
              <a
                href="/pitch/demo-video/SHOT_LIST.md"
                target="_blank"
                rel="noreferrer"
                className="inline-flex items-center gap-1.5 h-9 px-3 rounded-md text-xs font-semibold border border-zinc-700 text-zinc-400 hover:text-white"
                data-testid="demo-video-shot-list"
              >
                Shot list &amp; scripts
              </a>
            </div>
          </div>
        </div>
      </section>

      <footer className="border-t border-zinc-800 mt-12">
        <div className="max-w-6xl mx-auto px-6 md:px-10 py-6 flex items-center justify-between text-[11px] text-zinc-500">
          <span>Snagcart · The SMB Operating System</span>
          <span>Demo seeded for investor walkthroughs.</span>
        </div>
      </footer>
    </div>
  );
};

export default DemoPitch;
